/** Pure inbox rules shared by the store, the mock backend and the tests. No Vue imports. */
import type {
  Channel,
  ChannelFilter,
  ConversationDetail,
  ConversationKind,
  ConversationSummary,
  InboxSettings,
  InboxTab,
  LegacyInboxTab,
  Scope,
  TabCounts,
} from '../api/types'

export const DEFAULT_SETTINGS: InboxSettings = {
  reply_warn_sec: 120,
  reply_crit_sec: 180,
  follow_up_after_min: 60,
  assign_mode: 'least_busy',
  auto_off_min: 15,
}

/** Tab order on screen (owner 27 ก.ย. 69: ต้องตอบ + รอลูกค้า → กำลังคุย, then AI). */
export const TABS: readonly InboxTab[] = ['active', 'ai', 'follow_up', 'closed']

/** A message its sender unsent (type `unsent`, api/app/unsend.py) — same wording as the API's list preview. */
export const UNSENT_LABEL = { in: 'ลูกค้ายกเลิกข้อความนี้', out: 'ข้อความนี้ถูกยกเลิกแล้ว' } as const

export const TAB_LABEL: Record<InboxTab, string> = {
  active: 'กำลังคุย',
  ai: 'AI',
  follow_up: 'ทักตาม',
  closed: 'ปิดแล้ว',
}

/** The two groups of กำลังคุย, in this order (the old tab keys). */
export type ActiveGroup = LegacyInboxTab
export const GROUP_LABEL: Record<ActiveGroup, string> = { needs_reply: 'ต้องตอบ', waiting: 'รอลูกค้าตอบ' }

/** Where a chat is listed: one of the API's list segments (`tab=` values; the two groups of กำลังคุย are two). */
export type ListSegment = ActiveGroup | 'ai' | 'follow_up' | 'closed'

/** A tab key from anywhere (old links, old code, a stored value) → a tab on screen: the old two → กำลังคุย. */
export function normalizeTab(t: string | null | undefined): InboxTab {
  return (TABS as readonly string[]).includes(t ?? '') ? (t as InboxTab) : 'active'
}

const ms = (iso: string | null): number | null => (iso === null ? null : Date.parse(iso))

/**
 * Where a conversation is listed right now (first match wins; api/app/inbox.py has the same rules):
 * 1. open and the AI is in charge (docs/ai-mode.md)  → ai           (in no other tab)
 * 2. open and the customer spoke last                → needs_reply  (กำลังคุย › ต้องตอบ)
 * 3. follow-up due (silent past the set time) and    → follow_up    (also for auto-closed chats)
 *    still inside the follow-up window (`follow_up_until`, 7 days after our last message)
 * 4. open (we spoke last)                            → waiting      (กำลังคุย › รอลูกค้าตอบ)
 * 5. otherwise                                       → closed
 */
export function segmentOf(c: ConversationSummary, nowMs: number): ListSegment {
  if (c.status === 'open' && c.ai_mode) return 'ai'
  if (c.status === 'open' && c.needs_reply) return 'needs_reply'
  if (!c.needs_reply && followUpDue(c, nowMs)) return 'follow_up'
  if (c.status === 'open') return 'waiting'
  return 'closed'
}

/** Due for ทักตาม now: past its due time and not past the window (the API stops giving a due time from then). */
function followUpDue(c: ConversationSummary, nowMs: number): boolean {
  const due = ms(c.follow_up_due_at)
  if (due === null || due > nowMs) return false
  const until = ms(c.follow_up_until ?? null)
  return until === null || nowMs < until
}

/**
 * The next moment a loaded chat changes list on its own (no event comes for it): a follow-up due time, or the end of
 * its follow-up window. null = none ahead. stores/inbox.ts re-reads the list (and the tab numbers) just after it.
 */
export function nextListChange(items: Iterable<ConversationSummary>, nowMs: number): number | null {
  let next: number | null = null
  const consider = (t: number | null) => {
    if (t !== null && Number.isFinite(t) && t > nowMs && (next === null || t < next)) next = t
  }
  for (const c of items) {
    if (c.needs_reply) continue
    const due = ms(c.follow_up_due_at)
    if (due === null) continue
    consider(due)
    consider(ms(c.follow_up_until ?? null))
  }
  return next
}

/** Which list tab a conversation belongs to right now (see `segmentOf`). */
export function tabOf(c: ConversationSummary, nowMs: number): InboxTab {
  const s = segmentOf(c, nowMs)
  return s === 'needs_reply' || s === 'waiting' ? 'active' : s
}

/** A list tab of the API (`tab=`, old keys included) holds this chat now. */
export function inListTab(c: ConversationSummary, tab: InboxTab | LegacyInboxTab, nowMs: number): boolean {
  return tab === 'active' ? tabOf(c, nowMs) === 'active' : segmentOf(c, nowMs) === tab
}

/** Seconds the customer has been waiting for a reply, or null when nobody is waiting. */
export function waitSeconds(c: ConversationSummary, nowMs: number): number | null {
  const since = ms(c.waiting_since)
  if (!c.needs_reply || since === null) return null
  return Math.max(0, Math.floor((nowMs - since) / 1000))
}

export type WaitLevel = 'ok' | 'warn' | 'crit'

/** amber from `reply_warn_sec` (inclusive), red strictly over `reply_crit_sec`. */
export function waitLevel(sec: number | null, s: InboxSettings = DEFAULT_SETTINGS): WaitLevel {
  if (sec === null) return 'ok'
  if (sec > s.reply_crit_sec) return 'crit'
  if (sec >= s.reply_warn_sec) return 'warn'
  return 'ok'
}

export const WAIT_CLASS: Record<WaitLevel, string> = { ok: '', warn: 'w-warn', crit: 'w-crit' }

/** Short Thai duration: เมื่อกี้ · 2 นาที · 1 ชม. · 3 วัน */
export function formatDuration(sec: number): string {
  if (sec < 60) return 'เมื่อกี้'
  if (sec < 3600) return `${Math.floor(sec / 60)} นาที`
  if (sec < 86400) return `${Math.floor(sec / 3600)} ชม.`
  return `${Math.floor(sec / 86400)} วัน`
}

const TZ = 'Asia/Bangkok'
const fmtTime = new Intl.DateTimeFormat('th-TH', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ })
const fmtDay = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', timeZone: TZ })
const fmtDayYear = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ })
const fmtKey = new Intl.DateTimeFormat('en-CA', { timeZone: TZ })
const fmtMoney = new Intl.NumberFormat('th-TH')

export const clockTime = (iso: string): string => fmtTime.format(new Date(iso))
export const shortDate = (iso: string): string => fmtDay.format(new Date(iso))
export const dayKey = (msOrIso: number | string): string => fmtKey.format(new Date(msOrIso))
export const baht = (n: number): string => `฿${fmtMoney.format(n)}`

/** Relative time for rows outside "needs reply": เมื่อกี้ · 5 นาที · 3 ชม. · เมื่อวาน · 12 ส.ค. */
export function formatAgo(iso: string | null, nowMs: number): string {
  if (iso === null) return ''
  const t = Date.parse(iso)
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000))
  if (sec < 86400 && dayKey(t) === dayKey(nowMs)) return formatDuration(sec)
  if (dayKey(t) === dayKey(nowMs - 86400000)) return 'เมื่อวาน'
  return shortDate(iso)
}

/** Label for a thread day separator. */
export function dayLabel(iso: string, nowMs: number): string {
  const k = dayKey(iso)
  if (k === dayKey(nowMs)) return 'วันนี้'
  if (k === dayKey(nowMs - 86400000)) return 'เมื่อวาน'
  // another year (imported Facebook / LINE history goes back years): "12 ส.ค. 2567"
  if (k.slice(0, 4) !== dayKey(nowMs).slice(0, 4)) return fmtDayYear.format(new Date(iso))
  return shortDate(iso)
}

export function matchesChannel(c: { channel: Channel; kind: ConversationKind }, f: ChannelFilter): boolean {
  // comments live on their own page (/comments) — owner 26 ก.ย. "แยก": never in the chat inbox
  if (c.kind === 'comment') return false
  if (f === 'all') return true
  if (f === 'comment') return false
  return c.channel === f
}

export function matchesScope(c: ConversationSummary, scope: Scope, meId: string | null): boolean {
  return scope === 'team' || (meId !== null && c.assigned_staff?.id === meId)
}

/**
 * The scope a tab (or list segment) really uses. The AI tab always shows the whole team's AI chats (owner 27 ก.ย. 69) —
 * ทั้งทีม/ของฉัน only filters the other tabs. Same rule as api/app/inbox.py `scope_for` / `TEAM_ONLY`.
 */
export function scopeFor(tab: InboxTab | ListSegment | LegacyInboxTab, scope: Scope): Scope {
  return tab === 'ai' ? 'team' : scope
}

/** The ทั้งทีม/ของฉัน control does nothing on this tab (it is shown dimmed with SCOPE_AI_NOTE). */
export const scopeApplies = (tab: InboxTab): boolean => tab !== 'ai'
export const SCOPE_AI_NOTE = 'แท็บ AI แสดงแชทที่ AI ดูแลของทั้งทีมเสมอ · ทั้งทีม/ของฉัน ใช้กับแท็บอื่น'

export type SortMode = 'wait' | 'recent'

/**
 * กำลังคุย: "รอนานสุดก่อน" sorts its ต้องตอบ group; รอลูกค้าตอบ stays newest first (as the old รอลูกค้า tab did) —
 * same order as the API. AI: the chat touched last first.
 */
export const DEFAULT_SORT: Record<InboxTab, SortMode> = {
  active: 'wait',
  ai: 'recent',
  follow_up: 'wait',
  closed: 'recent',
}
/** The order of each list segment when nothing was toggled (the mock backend lists old tab keys with these). */
export const SEGMENT_SORT: Record<ListSegment, SortMode> = {
  needs_reply: 'wait',
  waiting: 'recent',
  ai: 'recent',
  follow_up: 'wait',
  closed: 'recent',
}

export const SORT_LABEL: Record<SortMode, string> = { wait: 'รอนานสุดก่อน', recent: 'ล่าสุดก่อน' }

/** The timestamp a list sorts on when "longest wait first". */
function waitKey(c: ConversationSummary, seg: ListSegment | InboxTab): number {
  const v =
    seg === 'needs_reply' || (seg === 'ai' && c.needs_reply)
      ? ms(c.waiting_since)
      : seg === 'follow_up'
        ? ms(c.follow_up_due_at)
        : ms(c.last_message_at)
  return v ?? Number.POSITIVE_INFINITY
}

function sortFlat(rows: ConversationSummary[], seg: ListSegment | InboxTab, mode: SortMode): ConversationSummary[] {
  const out = rows.slice()
  if (mode === 'wait') {
    out.sort((a, b) => waitKey(a, seg) - waitKey(b, seg) || a.id.localeCompare(b.id))
  } else {
    out.sort((a, b) => (ms(b.last_message_at) ?? 0) - (ms(a.last_message_at) ?? 0) || a.id.localeCompare(b.id))
  }
  return out
}

/** One block of the list on screen: กำลังคุย has two (ต้องตอบ, then รอลูกค้าตอบ), the other tabs one (`key` null). */
export interface RowGroup {
  key: ActiveGroup | null
  rows: ConversationSummary[]
}

/**
 * The rows of a tab in screen order. กำลังคุย: a row's group is its `needs_reply` (not the clock), so a row held in
 * place (stores/inbox.ts sticky) stays in the group it was in. The ต้องตอบ group follows the sort toggle; รอลูกค้าตอบ
 * is always newest first.
 */
export function arrangeRows(rows: ConversationSummary[], tab: InboxTab | LegacyInboxTab, mode: SortMode): RowGroup[] {
  if (tab !== 'active') return [{ key: null, rows: sortFlat(rows, tab, mode) }]
  return [
    { key: 'needs_reply', rows: sortFlat(rows.filter((c) => c.needs_reply), 'needs_reply', mode) },
    { key: 'waiting', rows: sortFlat(rows.filter((c) => !c.needs_reply), 'waiting', 'recent') },
  ]
}

export function sortRows(rows: ConversationSummary[], tab: InboxTab | LegacyInboxTab, mode: SortMode): ConversationSummary[] {
  return arrangeRows(rows, tab, mode).flatMap((g) => g.rows)
}

/**
 * Local rows a refreshed first page shows are no longer in that list (missed events): `local` = the rows this screen
 * holds for the list, `page` = the server's page in its order (DEFAULT_SORT). A complete list (no next page): every
 * missing row. Otherwise only the ones that would sort before the page's last row — later ones may be on a page
 * that was not re-read ("load more").
 */
export function goneFromPage(
  local: ConversationSummary[],
  page: ConversationSummary[],
  tab: InboxTab,
  complete: boolean,
): string[] {
  const got = new Set(page.map((c) => c.id))
  const missing = local.filter((c) => !got.has(c.id))
  if (complete) return missing.map((c) => c.id)
  const last = page[page.length - 1]
  if (!last || !missing.length) return []
  const order = sortRows([...page, ...missing], tab, DEFAULT_SORT[tab])
  const cut = order.findIndex((c) => c.id === last.id)
  return order.slice(0, Math.max(0, cut)).filter((c) => !got.has(c.id)).map((c) => c.id)
}

export function channelBadge(c: { channel: Channel; kind: ConversationKind }): { label: string; cls: string } {
  if (c.kind === 'comment') return { label: 'คอมเมนต์', cls: 'ch-cmt' }
  switch (c.channel) {
    case 'fb':
      return { label: 'FB', cls: 'ch-fb' }
    case 'ig':
      return { label: 'IG', cls: 'ch-ig' }
    case 'line':
      return { label: 'LINE', cls: 'ch-line' }
    case 'web':
      return { label: 'เว็บ', cls: 'ch-web' }
  }
}

export const CHANNEL_FILTERS: { value: ChannelFilter; label: string }[] = [
  { value: 'all', label: 'ทุกช่องทาง' },
  { value: 'fb', label: 'Messenger' },
  { value: 'ig', label: 'Instagram' },
  { value: 'line', label: 'LINE' },
  { value: 'web', label: 'แชทเว็บ' },
]

export function countTabs(
  items: Iterable<ConversationSummary>,
  nowMs: number,
  channel: ChannelFilter,
  scope: Scope,
  meId: string | null,
): TabCounts {
  const counts: TabCounts = { needs_reply: 0, waiting: 0, follow_up: 0, ai: 0 }
  for (const c of items) {
    if (!matchesChannel(c, channel)) continue
    const k = countKey(c, nowMs)
    if (k && matchesScope(c, scopeFor(k, scope), meId)) counts[k] = (counts[k] ?? 0) + 1
  }
  return counts
}

/** The tab-header number a chat adds to (the list segment; closed is not counted). */
export function countKey(c: ConversationSummary, nowMs: number): keyof TabCounts | null {
  const s = segmentOf(c, nowMs)
  return s === 'closed' ? null : s
}

/** Staff avatar colours, assigned by position in the team list (calm, AA on white text). */
export const STAFF_TONES = ['s-p', 's-j', 's-s', 's-w', 's-v'] as const

/** The list-row part of a detail (what the list endpoint and realtime events carry). */
export function toSummary(d: ConversationDetail): ConversationSummary {
  return {
    id: d.id,
    channel: d.channel,
    kind: d.kind,
    status: d.status,
    contact: { id: d.contact.id, name: d.contact.name, initial: d.contact.initial, avatar_url: d.contact.avatar_url },
    last_message: d.last_message ? { ...d.last_message } : null,
    last_message_at: d.last_message_at,
    needs_reply: d.needs_reply,
    waiting_since: d.waiting_since,
    follow_up_due_at: d.follow_up_due_at,
    ...(d.follow_up_until !== undefined ? { follow_up_until: d.follow_up_until } : {}),
    ...(d.auto_close_off !== undefined ? { auto_close_off: d.auto_close_off } : {}),
    ...(d.follow_up ? { follow_up: { ...d.follow_up } } : {}),
    unread: d.unread,
    assigned_staff: d.assigned_staff ? { ...d.assigned_staff } : null,
    tag: d.tag ? { ...d.tag } : null,
    ...(d.tags ? { tags: d.tags.map((t) => ({ ...t })) } : {}),
    hot: d.hot,
    ...(d.ai_mode !== undefined ? { ai_mode: d.ai_mode ? { ...d.ai_mode, by: { ...d.ai_mode.by } } : null } : {}),
    ...(d.ai_handoff !== undefined ? { ai_handoff: d.ai_handoff ? { ...d.ai_handoff } : null } : {}),
    ...(d.web_block !== undefined ? { web_block: d.web_block ? { ...d.web_block } : null } : {}),
    ...(d.style_key !== undefined ? { style_key: d.style_key } : {}),
  }
}
